// v10/src/utils/navigationCalculator.js
//
// Cálculo de plan de navegación en frontend. Port LITERAL del backend
// services/navigationService.js. Se usa para simulación en vivo en
// NavigationPlanner.js sin ir al backend.
//
// IMPORTANTE: si modificás la lógica acá, ACTUALIZÁ también
// condor360-backend/services/navigationService.js — el backend re-ejecuta
// el cálculo al guardar (POST /api/navigations) y NO confía en el resultado
// del frontend.

export const EARTH_RADIUS_NM = 3440.065;  // radio de la Tierra en millas náuticas

// Reserva ANAC RAAC 91: 45 minutos a TAS de crucero (VFR diurno)
export const ANAC_RESERVE_MINUTES = 45;

/**
 * Calcula un plan de navegación con waypoints.
 *
 * @param {Array<{icao, name, lat, lng}>} waypoints - secuencia ordenada
 *   (incluye origen + intermedios + destino, mínimo 2 puntos)
 * @param {Object} performance - { tas_knots, consumption_gph }
 * @param {Object} [options] - { wind: { direction_deg, speed_kt } | null }
 *
 * @returns {Object} { total_distance_nm, total_time_min, total_fuel_gal,
 *                     reserve_*, wind, legs }
 */
export function calculateNavigation(waypoints, performance, options = {}) {
  if (!Array.isArray(waypoints) || waypoints.length < 2) {
    throw new Error('Se requieren al menos 2 waypoints (origen + destino)');
  }

  const tas = Number(performance.tas_knots) || 0;
  const gph = Number(performance.consumption_gph) || 0;

  if (tas <= 0 || gph <= 0) {
    throw new Error('TAS y consumo deben ser > 0');
  }

  const wind = normalizeWind(options.wind);

  const legs = [];
  let totalDistanceNm = 0;
  let totalTimeMin = 0;
  let totalFuelGal = 0;

  for (let i = 0; i < waypoints.length - 1; i++) {
    const from = waypoints[i];
    const to = waypoints[i + 1];

    const distNm = haversineNm(from.lat, from.lng, to.lat, to.lng);
    const courseDeg = bearingDeg(from.lat, from.lng, to.lat, to.lng);

    // Efecto del viento (sin viento → wca=0, heading=course, gs=tas)
    const windEffect = applyWindToLeg(courseDeg, tas, wind);
    const effectiveSpeed = windEffect.ground_speed_kt;

    const timeMin = effectiveSpeed > 0 ? (distNm / effectiveSpeed) * 60 : 0;
    const fuelGal = (timeMin / 60) * gph;

    legs.push({
      from_icao: from.icao,
      to_icao: to.icao,
      from_name: from.name,
      to_name: to.name,
      from_lat: from.lat,
      from_lng: from.lng,
      to_lat: to.lat,
      to_lng: to.lng,
      distance_nm: round1(distNm),
      true_course_deg: Math.round(courseDeg),
      wca_deg: windEffect.wca_deg,
      heading_deg: windEffect.heading_deg,
      ground_speed_kt: windEffect.ground_speed_kt,
      time_min: Math.round(timeMin),
      fuel_gal: round2(fuelGal),
    });

    totalDistanceNm += distNm;
    totalTimeMin += timeMin;
    totalFuelGal += fuelGal;
  }

  // Cálculo de reserva ANAC (fijo 45 min a GPH de crucero)
  const reserveFuelGal = (ANAC_RESERVE_MINUTES / 60) * gph;
  const totalFuelWithReserveGal = totalFuelGal + reserveFuelGal;

  return {
    total_distance_nm: round1(totalDistanceNm),
    total_time_min: Math.round(totalTimeMin),
    total_fuel_gal: round2(totalFuelGal),
    reserve_minutes: ANAC_RESERVE_MINUTES,
    reserve_fuel_gal: round2(reserveFuelGal),
    total_fuel_with_reserve_gal: round2(totalFuelWithReserveGal),
    wind: wind ? {
      direction_deg: wind.direction_deg,
      speed_kt: wind.speed_kt,
    } : null,
    legs,
  };
}

/**
 * Normaliza un objeto wind. Devuelve null si no hay viento aplicable.
 */
function normalizeWind(wind) {
  if (!wind) return null;
  const dir = Number(wind.direction_deg);
  const spd = Number(wind.speed_kt);
  if (!Number.isFinite(dir) || !Number.isFinite(spd) || spd <= 0) return null;
  let d = dir % 360;
  if (d < 0) d += 360;
  return { direction_deg: d, speed_kt: spd };
}

/**
 * Calcula el efecto del viento sobre un leg.
 *
 * Convención del viento: "from" (de dónde viene). Ej: 270° = viento del oeste.
 */
export function applyWindToLeg(trueCourseDeg, tasKnots, wind) {
  const tas = Number(tasKnots) || 0;
  if (!wind || tas <= 0) {
    return {
      wca_deg: 0,
      heading_deg: Math.round(trueCourseDeg),
      ground_speed_kt: round1(tas),
    };
  }

  const windDir = Number(wind.direction_deg);
  const windSpd = Number(wind.speed_kt);
  if (!Number.isFinite(windDir) || !Number.isFinite(windSpd) || windSpd <= 0) {
    return {
      wca_deg: 0,
      heading_deg: Math.round(trueCourseDeg),
      ground_speed_kt: round1(tas),
    };
  }

  const toRad = d => d * Math.PI / 180;
  const toDeg = r => r * 180 / Math.PI;

  let windAngle = windDir - trueCourseDeg;
  while (windAngle > 180) windAngle -= 360;
  while (windAngle < -180) windAngle += 360;

  const sinArg = (windSpd * Math.sin(toRad(windAngle))) / tas;
  const clamped = Math.max(-1, Math.min(1, sinArg));
  const wcaRad = Math.asin(clamped);
  const wcaDeg = toDeg(wcaRad);

  const gsKt = tas * Math.cos(wcaRad) - windSpd * Math.cos(toRad(windAngle));

  let headingDeg = trueCourseDeg + wcaDeg;
  while (headingDeg < 0) headingDeg += 360;
  while (headingDeg >= 360) headingDeg -= 360;

  return {
    wca_deg: round1(wcaDeg),
    heading_deg: Math.round(headingDeg),
    ground_speed_kt: round1(Math.max(0, gsKt)),
  };
}

/**
 * Distancia haversine en millas náuticas.
 */
export function haversineNm(lat1, lng1, lat2, lng2) {
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_NM * c;
}

/**
 * Bearing inicial entre 2 puntos, en grados (0-360, 0=N, 90=E).
 */
export function bearingDeg(lat1, lng1, lat2, lng2) {
  const toRad = d => d * Math.PI / 180;
  const toDeg = r => r * 180 / Math.PI;
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const dLambda = toRad(lng2 - lng1);

  const y = Math.sin(dLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) -
            Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda);
  const theta = Math.atan2(y, x);
  return (toDeg(theta) + 360) % 360;
}

function round1(n) { return Math.round(n * 10) / 10; }
function round2(n) { return Math.round(n * 100) / 100; }
